<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { BFormGroup, BFormSelect, BTooltip } from 'bootstrap-vue-next';
import {
  CloudRegionPrefixSelectOptions,
  countryToRegionLabel,
} from '@/utils/cloud-utils';
import { useUserStore } from '@/store/user';

const userStore = useUserStore();

/* ───────────────────────── props / emits ───────────────────────── */
const props = defineProps<{
  /** selected region prefix (null -> default) */
  modelValue?: string | null;
  /** whether the custom selector is shown */
  modelRequired?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
  (e: 'update:modelRequired', value: boolean): void;
}>();

/* ───────────────────────── local state (writable copies) ───────── */
const prefix = ref<string | null>(props.modelValue ?? null);
const required = ref<boolean>(props.modelRequired ?? false);

/* sync locals ↔︎ parent */
watch(prefix, (v) => emit('update:modelValue', v));
watch(required, (v) => emit('update:modelRequired', v));

const defaultRegion = computed(() => {
  if (userStore.activeOrgAssociation?.organization?.addressCountryCode) {
    return countryToRegionLabel(
      userStore.activeOrgAssociation.organization.addressCountryCode
    );
  }
  return null;
});

// handlers
function toggleCustomCloudRegion() {
  required.value = !required.value;
}
</script>
<template>
  <div :class="[required ? 'expanded-group' : 'collapsed-group']">
    <label
      id="cloud-region-help-label"
      role="button"
      tabindex="0"
      style="cursor: pointer"
      @click.stop="toggleCustomCloudRegion"
      :class="required ? 'mb-2' : 'mb-0'"
    >
      <font-awesome-icon :icon="['fas', 'globe']" class="me-1" />
      <template v-if="required">
        Data Location
        <i class="fas fa-angle-up ms-1" />
      </template>
      <template v-else>
        Data Location:
        <span v-if="defaultRegion"> {{ defaultRegion }}</span>
        <span v-else>Based on your organization settings</span>
        <i class="fas fa-angle-down ms-1" />
        <br />
        <small>
          Double-check the location satisfies the regulatory requirements.
        </small>
      </template>
    </label>
    <b-tooltip target="cloud-region-help-label" v-if="!required">
      Click to change where your data will be stored or processed. Choose a
      region that meets any legal/compliance requirements and, if possible, is
      close to the data source to reduce transfer time.
    </b-tooltip>
    <b-form-group v-if="required">
      <b-form-select
        :options="CloudRegionPrefixSelectOptions"
        v-model="prefix"
        id="gcp-zone"
        name="gcp-zone"
      />
      <small>
        Select the region in which the data will be stored or processed. Pick a
        location that satisfies regulatory requirements and is geographically
        close to the data source to minimise transfer time.
      </small>
    </b-form-group>
  </div>
</template>
